// /home/coder/project/frontend/src/pages/qaqc/inspections/QaqcInspRecordList.tsx
import React, { useState, useMemo } from 'react';
import { Spin, Card, message, Typography, Popconfirm, Space, Tag, Input, Row, Col, Select } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PlusOutlined, AuditOutlined, EyeOutlined, EditOutlined, DeleteOutlined, ClearOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

import { deleteData } from '../../../services/api';
import { VwQaqcInspRecord } from '../../../types';
import { useGetVwQaqcInspRecords } from '../../../hooks/genericHooks';
import StandardTable from '../../../components/common/StandardTable';
import Button from '../../../components/common/Button';

const { Title } = Typography;
const { Option } = Select;

interface DataType extends VwQaqcInspRecord {
  key: React.Key;
}

const QaqcInspRecordList: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: recordsResponse, isLoading, isError, error } = useGetVwQaqcInspRecords();
  const recordsData = recordsResponse?.data;

  // State for external filters
  const [formNoFilter, setFormNoFilter] = useState<string>('');
  const [formNameFilter, setFormNameFilter] = useState<string>('');
  const [systemFilter, setSystemFilter] = useState<string>('');
  const [inspectorFilter, setInspectorFilter] = useState<string>('');
  // const [inspDateFilter, setInspDateFilter] = useState<[string | null, string | null] | null>(null); // For DateRangePicker if needed

  const deleteRecordMutation = useMutation<unknown, Error, number>({
    mutationFn: (recordId: number) => deleteData('qaqc_insp_forms', 'id', recordId),
    onSuccess: (_, recordId) => {
      message.success(`QA/QC Inspection Record (ID: ${recordId}) deleted successfully!`);
      queryClient.invalidateQueries({ queryKey: ['vwQaqcInspRecords'] });
    },
    onError: (err: any, recordId) => {
      console.error(`Error deleting QA/QC Inspection Record (ID: ${recordId}):`, err);
      const errorMsg = err.response?.data?.error || err.message || 'Failed to delete record.';
      message.error(`Error: ${errorMsg}`);
    },
  });

  const handleDelete = (recordId: number) => {
    deleteRecordMutation.mutate(recordId);
  };

  const handleAdd = () => {
    navigate('/qaqc/inspections/records/create');
  };

  const handleEdit = (recordId: number) => {
    navigate(`/qaqc/inspections/record_edit/${recordId}`);
    // You can uncomment the line below if the edit page isn't ready yet
    // message.info("Edit functionality for QA/QC Records is not yet implemented.");
  };

  const handleView = (recordId: number) => {
    navigate(`/qaqc/inspections/record_show/${recordId}`);
  };

  const filteredRecordsData = useMemo(() => {
    if (!recordsData) return [];
    return recordsData.filter(record => {
      const formNoMatch = formNoFilter ? record.form_design_num?.toLowerCase().includes(formNoFilter.toLowerCase()) : true;
      const formNameMatch = formNameFilter ? record.form_design_name?.toLowerCase().includes(formNameFilter.toLowerCase()) : true;
      const systemMatch = systemFilter ? record.system_name?.toLowerCase().includes(systemFilter.toLowerCase()) : true;
      const inspectorMatch = inspectorFilter ? record.inspector_full_name?.toLowerCase().includes(inspectorFilter.toLowerCase()) : true;
      // Add date filter logic here if inspDateFilter is implemented
      return formNoMatch && formNameMatch && systemMatch && inspectorMatch;
    });
  }, [recordsData, formNoFilter, formNameFilter, systemFilter, inspectorFilter]);

  // --- Prepare unique values for filter dropdowns ---
  const uniqueFormNos = useMemo(() => {
    if (!recordsData) return [];
    return Array.from(new Set(recordsData.map(r => r.form_design_num).filter(Boolean))).sort();
  }, [recordsData]);

  const uniqueFormNames = useMemo(() => {
    if (!recordsData) return [];
    return Array.from(new Set(recordsData.map(r => r.form_design_name).filter(Boolean))).sort();
  }, [recordsData]);

  const uniqueSystems = useMemo(() => {
    if (!recordsData) return [];
    return Array.from(new Set(recordsData.map(r => r.system_name).filter(Boolean))).sort();
  }, [recordsData]);

  const uniqueInspectors = useMemo(() => {
    if (!recordsData) return [];
    return Array.from(new Set(recordsData.map(r => r.inspector_full_name).filter(Boolean))).sort();
  }, [recordsData]);

  const columns: ColumnsType<DataType> = [
    {
      title: 'ID',
      dataIndex: 'record_id',
      key: 'record_id',
      width: 80, // Reasonably low width
      sorter: (a: VwQaqcInspRecord, b: VwQaqcInspRecord) => a.record_id - b.record_id,
    },
    {
      title: 'Insp. Date',
      dataIndex: 'insp_date',
      key: 'insp_date',
      render: (date: string) => dayjs(date).isValid() ? dayjs(date).format('YYYY-MM-DD') : 'Invalid Date',
      sorter: (a: VwQaqcInspRecord, b: VwQaqcInspRecord) => dayjs(a.insp_date).unix() - dayjs(b.insp_date).unix(),
    },
    {
      title: 'Form No.',
      dataIndex: 'form_design_num',
      key: 'form_design_num',
      sorter: (a: VwQaqcInspRecord, b: VwQaqcInspRecord) => a.form_design_num.localeCompare(b.form_design_num),
    },
    {
      title: 'Form Name',
      dataIndex: 'form_design_name',
      key: 'form_design_name',
      render: (_: any, record: VwQaqcInspRecord) => record.form_design_name,
      sorter: (a: VwQaqcInspRecord, b: VwQaqcInspRecord) => a.form_design_name.localeCompare(b.form_design_name),
    },
    {
      title: 'System',
      dataIndex: 'system_name',
      key: 'system_name',
      render: (_: any, record: VwQaqcInspRecord) => `${record.system_name} (${record.sys_id})`,
      sorter: (a: VwQaqcInspRecord, b: VwQaqcInspRecord) => a.system_name.localeCompare(b.system_name),
    },
    {
      title: 'Inspector',
      dataIndex: 'inspector_full_name',
      key: 'inspector_full_name',
      sorter: (a: VwQaqcInspRecord, b: VwQaqcInspRecord) => a.inspector_full_name.localeCompare(b.inspector_full_name),
    },
    {
      title: 'Result',
      dataIndex: 'form_result',
      key: 'form_result',
      render: (form_result: boolean) => (
        <Tag color={form_result ? 'success' : 'error'}>
          {form_result ? 'Pass' : 'Fail'}
        </Tag>
      ),
      filters: [
        { text: 'Pass', value: true },
        { text: 'Fail', value: false },
      ],
      onFilter: (value: boolean | React.Key, record: VwQaqcInspRecord) => record.form_result === value,
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right' as const,
      width: 120,
      render: (_: any, record: VwQaqcInspRecord) => (
        <Space>
          <Button action="read" type="link" icon={<EyeOutlined />} onClick={() => handleView(record.record_id)} size="small" title="View Record" />
          <Button action="edit" type="primary" icon={<EditOutlined />} onClick={() => handleEdit(record.record_id)} size="small" title="Edit Record" />
          <Popconfirm
            title="Are you sure you want to delete this record?"
            onConfirm={() => handleDelete(record.record_id)}
            okText="Yes"
            cancelText="No"
            disabled={deleteRecordMutation.isPending && deleteRecordMutation.variables === record.record_id}
          >
            <Button 
              action="delete"
              type="link" 
              danger 
              icon={<DeleteOutlined />} 
              loading={deleteRecordMutation.isPending && deleteRecordMutation.variables === record.record_id} 
              size="small" 
              title="Delete Record" 
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (isError && error) {
    message.error(`Error fetching QA/QC Inspection Records: ${error.message}`);
  }

  return (
    <Card
      title={
        <Title level={4} style={{ margin: 0 }}>
          <AuditOutlined style={{ marginRight: 8 }} />
          QA/QC Inspection Records
        </Title>
      }
      bordered={false}
      extra={<Button action="add" type="primary" icon={<PlusOutlined />} onClick={handleAdd}>Add New Record</Button>}
    >
      <Row gutter={[16, 16]} style={{ marginBottom: 8 }}>
        <Col xs={24} sm={12} md={6} lg={4}>
          <Select
            placeholder="Filter Form No."
            value={formNoFilter || undefined} // Use undefined for placeholder to show
            onChange={value => setFormNoFilter(value || '')}
            allowClear
            showSearch
            style={{ width: '100%' }}
            filterOption={(input, option) =>
              (option?.children as unknown as string ?? '').toLowerCase().includes(input.toLowerCase())
            }
          >
            {uniqueFormNos.map(item => <Option key={item} value={item}>{item}</Option>)}
          </Select>
        </Col>
        <Col xs={24} sm={12} md={6} lg={5}>
          <Select
            placeholder="Filter Form Name"
            value={formNameFilter || undefined}
            onChange={value => setFormNameFilter(value || '')}
            allowClear
            showSearch
            style={{ width: '100%' }}
            filterOption={(input, option) =>
              (option?.children as unknown as string ?? '').toLowerCase().includes(input.toLowerCase())
            }
          >
            {uniqueFormNames.map(item => <Option key={item} value={item}>{item}</Option>)}
          </Select>
        </Col>
        <Col xs={24} sm={12} md={6} lg={5}>
          <Select
            placeholder="Filter System"
            value={systemFilter || undefined}
            onChange={value => setSystemFilter(value || '')}
            allowClear
            showSearch
            style={{ width: '100%' }}
            filterOption={(input, option) =>
              (option?.children as unknown as string ?? '').toLowerCase().includes(input.toLowerCase())
            }
          >
            {uniqueSystems.map(item => <Option key={item} value={item}>{item}</Option>)}
          </Select>
        </Col>
        <Col xs={24} sm={12} md={6} lg={5}>
          <Select
            placeholder="Filter Inspector"
            value={inspectorFilter || undefined}
            onChange={value => setInspectorFilter(value || '')}
            allowClear
            showSearch
            style={{ width: '100%' }}
            filterOption={(input, option) =>
              (option?.children as unknown as string ?? '').toLowerCase().includes(input.toLowerCase())
            }
          >
            {uniqueInspectors.map(item => <Option key={item} value={item}>{item}</Option>)}
          </Select>
        </Col>
      </Row>
      <Spin spinning={isLoading || deleteRecordMutation.isPending}>
        <StandardTable
          columns={columns as any}
          dataSource={filteredRecordsData.map(item => ({ ...item, key: item.record_id }))}
          rowKey="record_id"
          bordered
          size="small"
          scroll={{ x: 'max-content' }}
          pagination={{ 
            pageSize: 20, 
            showSizeChanger: true, 
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
        />
      </Spin>
    </Card>
  );
};

export default QaqcInspRecordList;